import { Injectable, EventEmitter } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Post, Message, CallData, NotificationMessage, InteractionUpdate } from '../core/models';
import { HeaderService } from './header.service';
import { AuthService } from './auth.service';
import { Observable, of, throwError, BehaviorSubject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ToasterService } from './toaster.service';
import { UserService } from '../modules/feature/user/user.service';


@Injectable({
    providedIn: 'root'
})


export class SignalrService {

    private get session() { return this.header.session; }
    private connection: signalR.HubConnection;
    private startPromise: Promise<void> = null;
    private reconnectTimer: ReturnType<typeof setTimeout> = null;
    private reconnectAttempt = 0;
    private refreshInProgress = false;
    private stopped = true;
    private readonly reconnectDelays = [2000, 5000, 10000, 30000];
    public post = new EventEmitter<Post>();
    public message = new EventEmitter<Message>();
    public notifications = new BehaviorSubject<NotificationMessage>({});
    public messageNotification = new BehaviorSubject<Message>({});
    public interactionUpdate = new BehaviorSubject<InteractionUpdate>({});
    public postThread = new EventEmitter<string>();
    public callData = new EventEmitter<CallData>();
    public messageThreadSource = new BehaviorSubject<any[]>([])
    messageThread$ = this.messageThreadSource.asObservable();

    public messageSource = new BehaviorSubject<any[]>([])
    message$ = this.messageSource.asObservable();

    public chatWidgetMessages = new BehaviorSubject<any[]>([])
    chatMessages$ = this.chatWidgetMessages.asObservable();

    public chatWidgetMessageList = new BehaviorSubject<any[]>([])
    chatWigdetList$ = this.chatWidgetMessageList.asObservable();

    public currentlyTyping = new BehaviorSubject<any>({})
    senderIsTyping$ = this.currentlyTyping.asObservable();

    users$ = new BehaviorSubject([]);
    readonly coords: string = this.userService.get('coords');
    messageList: any[] = [];
    messages: any[] = [];
    messg: any = {};
    messageReceived = new EventEmitter<any>();
    messageThreadUpdateList = new EventEmitter<any>();
    messageId = ""


    constructor(
        private header: HeaderService,
        private authService: AuthService,
        private toastservice: ToasterService,
        private userService: UserService) { }

    // method to hit from client
    public Teleport(param: any) {
        if (this.authService.isAuthenticated()) {
            if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
                this.start();
                this.startConnection().then(() => {
                    if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
                        this.Teleport(param);
                    }
                });
            }

            else {
                this.connection.invoke(param.method, param.channel, JSON.stringify(param.data))
                    .catch(error => {
                        if (!environment.production) {
                            console.warn('DEBUG: Signalr => server method invocation failed : ' + param.method + ' - ' + JSON.stringify(error));
                        }

                        this.handleError(error).subscribe(
                            () => { if (error.data && error.data.status == 401) { this.Teleport(param); } }, // handle server side 401
                        );
                    });
            }
        }

        else { // handle client side 401
            this.handle401().subscribe(
                () => this.Teleport(param),
                () => {
                    if (!environment.production) console.warn('DEBUG: Signalr => token renewal error on server method invocation');
                }
            );
        }
    }

    public Transmit(channel: string, data: any) {
        this.Teleport({ method: 'Transmit', channel: channel, data: data });
    }

    public retrieveMessage(): Observable<Message> {
        return this.message.asObservable();
    }

    public retrivePostThread(): Observable<string> {
        return this.postThread.asObservable();
    }

    public retrieveNotification(): Observable<NotificationMessage> {
        return this.notifications.asObservable();
    }

    public retreiveMessages(): Observable<Message> {
        return this.messageNotification.asObservable()
    }

    private setupConnection() {
        if (this.connection) {
            return;
        }

        this.connection = new signalR.HubConnectionBuilder().
            withUrl(`${environment.centralsocket}`, {
                accessTokenFactory: () => {
                    return this.header.get_signalr_qs()['Access-Token'] || '';
                } // If 2036 characters or greater, 404 responses is issued, configure API to increase querystring
            }).
            withAutomaticReconnect([0, 2000, 10000, 30000]).
            configureLogging(signalR.LogLevel.Information).build();

        this.connection.onreconnected(() => {
            this.reconnectAttempt = 0;
            this.clearReconnectTimer();
        });

        this.connection.onclose(() => {
            this.startPromise = null;
            this.scheduleReconnect();
        });

        this.registerOnServerEvents();
    }

    private startConnection(): Promise<void> {
        if (this.stopped || !this.isInSession()) {
            return Promise.resolve();
        }

        this.setupConnection();

        if (this.connection.state === signalR.HubConnectionState.Connected ||
            this.connection.state === signalR.HubConnectionState.Connecting ||
            this.connection.state === signalR.HubConnectionState.Reconnecting) {
            return this.startPromise || Promise.resolve();
        }

        if (this.startPromise) {
            return this.startPromise;
        }

        if (!this.authService.isAuthenticated()) {
            if (!this.refreshInProgress) {
                this.refreshInProgress = true;
                const refresh = this.handle401();
                refresh.subscribe(
                    () => {
                        this.refreshInProgress = false;
                        this.startConnection();
                    },
                    () => {
                        this.refreshInProgress = false;
                        this.scheduleReconnect();
                        if (!environment.production) {
                            console.warn('DEBUG: SignalR token renewal failed');
                        }
                    }
                );
            }

            return Promise.resolve();
        }

        const pendingStart = this.connection.start()
            .then(() => {
                this.reconnectAttempt = 0;
                this.clearReconnectTimer();
            })
            .catch(error => {
                if (!environment.production) {
                    console.warn('DEBUG: SignalR unable to connect => ' + JSON.stringify(error));
                }
                this.scheduleReconnect();
            });

        this.startPromise = pendingStart.then(
            () => { this.startPromise = null; },
            error => {
                this.startPromise = null;
                throw error;
            }
        );

        return this.startPromise;
    }

    private scheduleReconnect() {
        if (this.stopped || !this.isInSession() || this.reconnectTimer ||
            (this.connection && this.connection.state !== signalR.HubConnectionState.Disconnected)) {
            return;
        }

        const delayIndex = Math.min(this.reconnectAttempt, this.reconnectDelays.length - 1);
        const delay = this.reconnectDelays[delayIndex];
        this.reconnectAttempt++;
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.startConnection();
        }, delay);
    }

    private clearReconnectTimer() {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }

    public start() {
        if (!this.isInSession()) {
            return;
        }

        this.stopped = false;
        this.setupConnection();
        this.startConnection();
    }

    public stop() {
        this.stopped = true;
        this.reconnectAttempt = 0;
        this.clearReconnectTimer();

        if (this.connection && this.connection.state !== signalR.HubConnectionState.Disconnected) {
            this.connection.stop().catch(error => {
                if (!environment.production) {
                    console.warn('DEBUG: SignalR stop failed => ' + JSON.stringify(error));
                }
            });
        }
    }

    private isInSession(): boolean { return this.session && this.session.token ? true : false; } // check this to avoid reconnection attempt after logout

    private isDisconnected(): boolean {
        return !this.connection || this.connection.state === signalR.HubConnectionState.Disconnected;
    }

    private handleError(error): Observable<any> {
        if (error.source === 'HubException') {

            console.error('SignalR Hub Exception => ' + error.message + ' : ' + error.data.status + ' - ' + error.data.userMessage);

            switch (error.data.status) {
                case 401:
                    return this.handle401();
                case 403:
                case 500:
                    return throwError(error);
                default:
                    return throwError(error);
            }
        }

        else {
            console.error('Unknown signalr error : ' + JSON.stringify(error));
            return throwError(error);
        }
    }

    private handle401(): Observable<any> {

        const refreshToken = this.header.refreshToken;

        if (refreshToken) {
            return this.authService.new_access_token(refreshToken).pipe(
                map(success => success),
                catchError(refreshTokenError => {
                    if (!environment.production) this.toastservice.warning('DEBUG: SignalR => login or session restart required');

                    throw refreshTokenError;
                })
            );
        }

        return throwError(new Error('SignalR token renewal requires a refresh token'));
    }

    private registerOnServerEvents() {
        this.connection.on("push", (channel: string, event: any) => {
            switch (channel) {
                case 'post':
                    this.post.next(event);
                    break;
                case 'message':
                    //used to track duplicate messages
                    if (event.id != this.messageId) {
                        this.messageId = event.id
                        this.message.emit(event);
                        if(event.isRetracted === null)
                        { this.messageNotification.next(event) }
                    }
                    break;
                case 'call':
                    this.callData.next(event);
                    break;

                case 'notifications':
                    this.notifications.next(event);
                    break;

                case 'postThread':
                    this.postThread.emit(event);
                    break;

                case 'interaction':
                    this.interactionUpdate.next(event)
                    break

                case 'userInputAction':
                    this.currentlyTyping.next(event)
                    break

                default:
                    // default code here
                    break;
            }
        });

        this.connection.on('showMessage', (type: string, msg: string) => {
            if (!environment.production) {
                switch (type) {
                    case 'info':
                        this.toastservice.info('Realtime Connection Established');
                        break;
                    case 'warning':
                    case 'error':
                        console.warn('DEBUG: SignalR => ' + JSON.stringify(msg));
                        break;
                }
            }
        });
    }

}
